import { createContextPane } from '../../components/ContextPane';
import type {
  ConferenceIndex,
  Footnote,
  GuidedThread,
  HighlightColor,
  Paragraph as TalkParagraph,
  ReaderTalkAnalysis,
  ScriptureSelection,
  StudyNote,
  Talk,
  TalkStudyState,
  TalkReflection,
} from '../../data/schema';
import {
  StudyStoreError,
  type StudyStore,
} from '../../services/studyStore';
import type { BrowserRouter } from '../../app/router';
import { createAnalysisRail } from './AnalysisRail';
import {
  createParagraph,
  type ParagraphView,
  type TextSelection,
} from './Paragraph';
import { createTalkHeader } from './TalkHeader';
import { createVerseHistory } from '../scriptures/VerseHistory';
import { createReflectionPanel } from './ReflectionPanel';
import { setShareMetadata } from '../../services/shareMetadata';

interface TalkReaderOptions {
  talk: Talk;
  index: ConferenceIndex;
  analysis: ReaderTalkAnalysis | undefined;
  reflection: TalkReflection;
  activeThread?: GuidedThread;
  targetParagraphId?: string;
  router: BrowserRouter;
  studyStore: StudyStore;
}

const HIGHLIGHT_LABELS: Record<HighlightColor, string> = {
  yellow: 'Key insight',
  blue: 'Scripture or doctrine connection',
  green: 'Personal application or action item',
  pink: 'Quote to remember',
};

function routerLink(
  href: string,
  label: string,
  className?: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  if (className) link.className = className;
  return link;
}

function externalLink(
  href: string,
  label: string,
  className?: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noreferrer';
  link.textContent = label;
  if (className) link.className = className;
  return link;
}

function createReaderHeading(talk: Talk, conferenceTitle: string): HTMLElement {
  const header = document.createElement('header');
  header.className = 'talk-heading';

  const session = document.createElement('p');
  session.className = 'eyebrow eyebrow--gold';
  session.textContent = `${talk.session.name} · ${conferenceTitle}`;
  const title = document.createElement('h1');
  title.textContent = talk.title;
  const speaker = document.createElement('p');
  speaker.className = 'talk-heading__speaker';
  const speakerName = document.createElement('strong');
  speakerName.textContent = talk.speaker.name;
  const calling = document.createElement('span');
  calling.textContent = talk.speaker.calling;
  speaker.append(speakerName, calling);

  const labels = document.createElement('div');
  labels.className = 'source-labels';
  const officialLabel = document.createElement('span');
  officialLabel.className = 'source-label source-label--official';
  officialLabel.textContent = 'Official Church text';
  labels.append(
    officialLabel,
    externalLink(talk.sourceUrl, 'View official source', 'text-link'),
  );

  const badges = document.createElement('div');
  badges.className = 'talk-badges';
  const footnotes = document.createElement('span');
  footnotes.textContent = `${String(talk.footnotes.length)} published footnotes`;
  const paragraphs = document.createElement('span');
  paragraphs.textContent = `${String(talk.paragraphs.length)} paragraphs`;
  badges.append(footnotes, paragraphs);
  header.append(session, title, speaker, labels, badges);

  if (talk.summary) {
    const summary = document.createElement('details');
    summary.className = 'talk-summary';
    const summaryLabel = document.createElement('summary');
    summaryLabel.textContent = 'Church News summary';
    const source = document.createElement('p');
    source.className = 'source-label source-label--news';
    source.textContent = `Source: ${talk.summary.attribution}`;
    const body = document.createElement('p');
    body.textContent = talk.summary.text;
    summary.append(
      summaryLabel,
      source,
      body,
      externalLink(talk.summary.sourceUrl, 'Read the Church News summary', 'text-link'),
    );
    header.append(summary);
  }
  return header;
}

function createFootnoteBody(
  footnote: Footnote,
  onOpenVerse: (scripture: ScriptureSelection) => void,
): HTMLElement {
  const body = document.createElement('div');
  body.className = 'footnote-context';
  const label = document.createElement('p');
  label.className = 'footnote-context__label';
  label.textContent =
    footnote.label ?? `Published footnote ${String(footnote.number)}`;
  body.append(label);

  if (footnote.scriptures.length > 0) {
    const list = document.createElement('ul');
    for (const scripture of footnote.scriptures) {
      const item = document.createElement('li');
      const history = document.createElement('button');
      history.type = 'button';
      history.className = 'scripture-history-trigger';
      history.textContent = scripture.displayLabel;
      history.setAttribute(
        'aria-label',
        `Open citation history for ${scripture.displayLabel}`,
      );
      history.addEventListener('click', () => onOpenVerse(scripture));
      item.append(
        history,
        externalLink(
          scripture.officialChurchUrl,
          'Gospel Library',
        ),
      );
      list.append(item);
    }
    body.append(list);
  }
  if (footnote.sourceUrl) {
    body.append(
      externalLink(footnote.sourceUrl, 'Open the published source', 'button'),
    );
  }
  if (footnote.scriptures.length === 0) {
    const context = document.createElement('p');
    context.className = 'muted';
    context.textContent =
      'This published note does not contain an exact normalized scripture selection.';
    body.append(context);
  }
  return body;
}

function createShareBody(url: string): HTMLElement {
  const body = document.createElement('div');
  body.className = 'share-context';
  const copy = document.createElement('p');
  copy.textContent = 'Use this stable link to return to the same reading position.';
  const field = document.createElement('input');
  field.type = 'text';
  field.readOnly = true;
  field.value = url;
  field.setAttribute('aria-label', 'Share link');
  field.addEventListener('focus', () => field.select());
  const button = document.createElement('button');
  button.className = 'button';
  button.type = 'button';
  button.textContent = 'Copy link';
  const status = document.createElement('p');
  status.className = 'inline-status';
  status.setAttribute('role', 'status');
  button.addEventListener('click', () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(url);
        status.textContent = 'Link copied.';
      } catch {
        field.select();
        status.textContent = 'Select and copy the link above.';
      }
    })();
  });
  body.append(copy, field, button, status);
  return body;
}

function createReadNavigation(
  index: ConferenceIndex,
  talkId: string,
  activeThread?: GuidedThread,
): HTMLElement {
  const navigation = document.createElement('nav');
  navigation.className = 'reader-sequence';
  navigation.setAttribute('aria-label', 'Talk sequence');
  const orderedTalkIds =
    activeThread?.steps.map((step) => step.talkId) ??
    index.talks.map((talk) => talk.talkId);
  const position = orderedTalkIds.indexOf(talkId);
  const suffix = activeThread
    ? `?thread=${encodeURIComponent(activeThread.threadId)}`
    : '';
  if (position > 0) {
    const previous = index.talks.find(
      (talk) => talk.talkId === orderedTalkIds[position - 1],
    );
    if (previous) {
      navigation.append(
        routerLink(
          `/read/${previous.talkId}${suffix}`,
          `Previous: ${previous.title}`,
          'reader-sequence__link',
        ),
      );
    }
  } else {
    navigation.append(document.createElement('span'));
  }
  if (position >= 0 && position < orderedTalkIds.length - 1) {
    const next = index.talks.find(
      (talk) => talk.talkId === orderedTalkIds[position + 1],
    );
    if (next) {
      navigation.append(
        routerLink(
          `/read/${next.talkId}${suffix}`,
          `Next: ${next.title}`,
          'reader-sequence__link reader-sequence__link--next',
        ),
      );
    }
  }
  return navigation;
}

function createStudyUnavailableMessage(error: unknown): HTMLElement {
  const message = document.createElement('div');
  message.className = 'source-callout';
  if (error instanceof StudyStoreError && error.status === 401) {
    const copy = document.createElement('p');
    copy.textContent =
      'Sign in to save reading progress, highlights, and paragraph notes.';
    message.append(copy, externalLink('/auth/google', 'Sign in', 'button'));
  } else {
    message.textContent =
      'Study tools could not reach the server. Reading remains available.';
  }
  return message;
}

export function createReadIndex(index: ConferenceIndex): HTMLElement {
  const page = document.createElement('div');
  page.className = 'read-index page-width';
  page.dataset.route = 'read-index';
  const heading = document.createElement('header');
  heading.className = 'read-index__heading';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Read';
  const title = document.createElement('h1');
  title.textContent = 'Choose a conference talk';
  const copy = document.createElement('p');
  copy.textContent =
    'Read the complete official text, then open footnotes and analysis only when useful.';
  heading.append(eyebrow, title, copy);

  const list = document.createElement('div');
  list.className = 'read-index__list';
  for (const talk of index.talks) {
    const card = document.createElement('article');
    card.className = 'card read-index-card';
    const session = document.createElement('p');
    session.className = 'eyebrow eyebrow--gold';
    session.textContent = talk.session.name;
    const talkTitle = document.createElement('h2');
    talkTitle.append(routerLink(`/read/${talk.talkId}`, talk.title));
    const speaker = document.createElement('p');
    speaker.textContent = talk.speaker.name;
    card.append(session, talkTitle, speaker);
    list.append(card);
  }
  page.append(heading, list);
  return page;
}

export function createTalkReader(options: TalkReaderOptions): HTMLElement {
  const {
    talk,
    index,
    analysis,
    reflection,
    activeThread,
    router,
    studyStore,
  } = options;
  const page = document.createElement('div');
  page.className = 'reader-page';
  page.dataset.route =
    options.targetParagraphId === undefined
      ? 'read-talk'
      : 'read-paragraph';
  page.dataset.talkId = talk.talkId;

  const workspace = document.createElement('div');
  workspace.className = 'reader-workspace';
  const mainColumn = document.createElement('div');
  mainColumn.className = 'reader-workspace__main';
  const pane = createContextPane({
    onOpenChange(isOpen) {
      workspace.classList.toggle('reader-workspace--context-open', isOpen);
    },
  });
  const liveStatus = document.createElement('p');
  liveStatus.className = 'reader-live-status';
  liveStatus.setAttribute('role', 'status');
  liveStatus.setAttribute('aria-live', 'polite');

  const threadSuffix = activeThread
    ? `?thread=${encodeURIComponent(activeThread.threadId)}`
    : '';
  const talkHeader = createTalkHeader(
    talk,
    index,
    (talkId) => {
      router.navigate(`/read/${talkId}${threadSuffix}`);
    },
    activeThread
      ? {
          href: `/threads/${activeThread.threadId}`,
          label: `Back to ${activeThread.title}`,
        }
      : undefined,
  );
  page.append(talkHeader.element);

  const layout = document.createElement('div');
  layout.className = 'reader-layout page-width';
  const article = document.createElement('article');
  article.className = 'talk-article';
  article.append(createReaderHeading(talk, index.title));

  let studyState: TalkStudyState = {
    progress: null,
    highlights: [],
    notes: [],
  };
  let studyAvailable = false;
  const paragraphViews = new Map<string, ParagraphView>();

  const refreshParagraph = (paragraphId: string): void => {
    paragraphViews.get(paragraphId)?.updateStudyState(
      studyState.highlights.filter(
        (highlight) => highlight.paragraphId === paragraphId,
      ),
      studyState.notes.find((note) => note.paragraphId === paragraphId),
    );
  };

  const openStudyError = (error: unknown, anchor: HTMLElement): void => {
    pane.replace({
      id: `study-error:${talk.talkId}`,
      title: 'Study tools',
      sourceLabel: 'Server study storage',
      body: createStudyUnavailableMessage(error),
      trigger: anchor,
    });
  };

  const openShare = (
    paragraph: TalkParagraph | undefined,
    anchor: HTMLElement,
  ): void => {
    const pathname = paragraph
      ? `/read/${talk.talkId}/${paragraph.id}`
      : `/read/${talk.talkId}`;
    const url = new URL(
      `${pathname}${threadSuffix}`,
      window.location.origin,
    ).toString();
    pane.replace({
      id: paragraph ? `share:${paragraph.id}` : `share:${talk.talkId}`,
      title: paragraph
        ? `Share paragraph ${String(paragraph.index + 1)}`
        : 'Share this talk',
      sourceLabel: 'Stable reading link',
      body: createShareBody(url),
      trigger: anchor,
    });
  };

  const openHighlight = (
    paragraph: TalkParagraph,
    selection: TextSelection,
    anchor: HTMLElement,
  ): void => {
    const body = document.createElement('div');
    body.className = 'highlight-context';
    const excerpt = document.createElement('blockquote');
    excerpt.textContent = selection.text;
    const palette = document.createElement('div');
    palette.className = 'highlight-palette';
    for (const color of Object.keys(HIGHLIGHT_LABELS) as HighlightColor[]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `highlight-choice highlight-choice--${color}`;
      button.textContent = HIGHLIGHT_LABELS[color];
      button.addEventListener('click', () => {
        void (async () => {
          try {
            const highlight = await studyStore.addHighlight({
              talkId: talk.talkId,
              paragraphId: paragraph.id,
              startOffset: selection.startOffset,
              endOffset: selection.endOffset,
              color,
              textSnippet: selection.text,
            });
            studyState.highlights.push(highlight);
            refreshParagraph(paragraph.id);
            liveStatus.textContent = `${HIGHLIGHT_LABELS[color]} highlight saved.`;
            pane.close();
          } catch (error) {
            openStudyError(error, anchor);
          }
        })();
      });
      palette.append(button);
    }
    const storage = document.createElement('p');
    storage.className = 'muted';
    storage.textContent = studyAvailable
      ? 'This highlight will be saved to your account.'
      : 'Sign in is required before this highlight can be saved.';
    body.append(excerpt, palette, storage);
    pane.replace({
      id: `highlight:${paragraph.id}`,
      title: 'Highlight this passage',
      sourceLabel: 'Personal study',
      body,
      trigger: anchor,
    });
  };

  const openNote = (
    paragraph: TalkParagraph,
    note: StudyNote | undefined,
    anchor: HTMLElement,
  ): void => {
    const form = document.createElement('form');
    form.className = 'note-form';
    const label = document.createElement('label');
    label.htmlFor = `note-${paragraph.id}`;
    label.textContent = `Note for paragraph ${String(paragraph.index + 1)}`;
    const textarea = document.createElement('textarea');
    textarea.id = `note-${paragraph.id}`;
    textarea.rows = 6;
    textarea.required = true;
    textarea.value = note?.noteText ?? '';
    const actions = document.createElement('div');
    actions.className = 'note-form__actions';
    const save = document.createElement('button');
    save.className = 'button';
    save.type = 'submit';
    save.textContent = 'Save note';
    actions.append(save);
    if (note) {
      const remove = document.createElement('button');
      remove.className = 'button button--secondary';
      remove.type = 'button';
      remove.textContent = 'Delete note';
      remove.addEventListener('click', () => {
        void (async () => {
          try {
            await studyStore.removeNote(talk.talkId, paragraph.id);
            studyState.notes = studyState.notes.filter(
              (item) => item.paragraphId !== paragraph.id,
            );
            refreshParagraph(paragraph.id);
            liveStatus.textContent = 'Paragraph note deleted.';
            pane.close();
          } catch (error) {
            openStudyError(error, anchor);
          }
        })();
      });
      actions.append(remove);
    }
    form.append(label, textarea, actions);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void (async () => {
        try {
          const savedNote = await studyStore.saveNote({
            talkId: talk.talkId,
            paragraphId: paragraph.id,
            noteText: textarea.value,
          });
          studyState.notes = [
            ...studyState.notes.filter(
              (item) => item.paragraphId !== paragraph.id,
            ),
            savedNote,
          ];
          refreshParagraph(paragraph.id);
          liveStatus.textContent = 'Paragraph note saved.';
          pane.close();
        } catch (error) {
          openStudyError(error, anchor);
        }
      })();
    });
    pane.replace({
      id: `note:${paragraph.id}`,
      title: note ? 'Edit paragraph note' : 'Add paragraph note',
      sourceLabel: 'Personal study',
      body: form,
      trigger: anchor,
    });
  };

  const footnotesByNumber = new Map(
    talk.footnotes.map((footnote) => [footnote.number, footnote]),
  );
  const openVerse = (scripture: ScriptureSelection): void => {
    void studyStore
      .saveExploredScripture(
        scripture.bookId,
        scripture.chapter,
        scripture.verses,
      )
      .catch(() => undefined);
    pane.push({
      id: `verse:${scripture.bookId}-${String(scripture.chapter)}-${scripture.verses.join('-')}`,
      title: scripture.displayLabel,
      sourceLabel: 'Scripture Citation Index history',
      body: createVerseHistory({
        selection: scripture,
        router,
        showFullRouteLink: true,
      }),
    });
  };
  for (const paragraph of talk.paragraphs) {
    const paragraphFootnotes = paragraph.footnoteNumbers
      .map((number) => footnotesByNumber.get(number))
      .filter((footnote): footnote is Footnote => footnote !== undefined);
    const view = createParagraph(
      talk.talkId,
      paragraph,
      paragraphFootnotes,
      [],
      undefined,
      {
        onFootnote(footnote, anchor) {
          pane.replace({
            id: `footnote:${String(footnote.number)}`,
            title: `Published footnote ${String(footnote.number)}`,
            sourceLabel: 'Official Church publication',
            body: createFootnoteBody(footnote, openVerse),
            trigger: anchor,
          });
        },
        onShare: openShare,
        onHighlight: openHighlight,
        onNote: openNote,
      },
    );
    paragraphViews.set(paragraph.id, view);
    article.append(view.element);
  }

  const analysisRail = createAnalysisRail({
    talkId: talk.talkId,
    analysis,
    onOpen(title, body, anchor) {
      pane.replace({
        id: `analysis:${talk.talkId}:${title.toLowerCase().replaceAll(' ', '-')}`,
        title,
        sourceLabel: 'AI analysis',
        body,
        trigger: anchor,
      });
    },
  });
  layout.append(analysisRail, article);

  article.append(
    createReflectionPanel({
      talk,
      reflection,
      activeThread,
      studyStore,
    }),
  );

  const sequence = createReadNavigation(index, talk.talkId, activeThread);
  const toolbar = document.createElement('div');
  toolbar.className = 'reader-toolbar';
  toolbar.setAttribute('aria-label', 'Reader tools');
  const analysisButton = document.createElement('button');
  analysisButton.type = 'button';
  analysisButton.textContent = 'Analysis';
  analysisButton.setAttribute('aria-label', 'Show theme and sentiment analysis');
  analysisButton.addEventListener('click', () => {
    const railButton = analysisRail.querySelector<HTMLButtonElement>('button');
    railButton?.click();
  });
  const highlightHelp = document.createElement('button');
  highlightHelp.type = 'button';
  highlightHelp.textContent = 'Highlight';
  highlightHelp.setAttribute('aria-label', 'How to highlight a passage');
  highlightHelp.addEventListener('click', () => {
    const body = document.createElement('p');
    body.textContent =
      'Select text in a paragraph, then choose its Highlight command. Without a selection, the whole paragraph is highlighted.';
    pane.replace({
      id: 'highlight-help',
      title: 'Highlight a passage',
      sourceLabel: 'Personal study',
      body,
      trigger: highlightHelp,
    });
  });
  const noteHelp = document.createElement('button');
  noteHelp.type = 'button';
  noteHelp.textContent = 'Note';
  noteHelp.setAttribute('aria-label', 'How to add a paragraph note');
  noteHelp.addEventListener('click', () => {
    const body = document.createElement('p');
    body.textContent =
      'Use the Note command beside any paragraph to attach a private study note.';
    pane.replace({
      id: 'note-help',
      title: 'Add a paragraph note',
      sourceLabel: 'Personal study',
      body,
      trigger: noteHelp,
    });
  });
  const shareTalk = document.createElement('button');
  shareTalk.type = 'button';
  shareTalk.textContent = 'Share';
  shareTalk.setAttribute('aria-label', 'Share this talk');
  shareTalk.addEventListener('click', () => openShare(undefined, shareTalk));
  const bookmarkTalk = document.createElement('button');
  bookmarkTalk.type = 'button';
  bookmarkTalk.textContent = 'Bookmark';
  bookmarkTalk.setAttribute('aria-label', 'Bookmark this talk');
  bookmarkTalk.setAttribute('aria-pressed', 'false');
  let bookmarked = false;
  const updateBookmark = (): void => {
    bookmarkTalk.textContent = bookmarked ? 'Bookmarked' : 'Bookmark';
    bookmarkTalk.setAttribute('aria-pressed', String(bookmarked));
    bookmarkTalk.setAttribute(
      'aria-label',
      bookmarked ? 'Remove this talk bookmark' : 'Bookmark this talk',
    );
  };
  bookmarkTalk.addEventListener('click', () => {
    bookmarkTalk.disabled = true;
    void (bookmarked
      ? studyStore.removeBookmark(talk.talkId)
      : studyStore.saveBookmark(talk.talkId)
    )
      .then(() => {
        bookmarked = !bookmarked;
        updateBookmark();
        liveStatus.textContent = bookmarked
          ? 'Talk bookmarked.'
          : 'Talk bookmark removed.';
      })
      .catch((error: unknown) => {
        openStudyError(error, bookmarkTalk);
      })
      .finally(() => {
        bookmarkTalk.disabled = false;
      });
  });
  void studyStore
    .loadBookmarks()
    .then((bookmarks) => {
      bookmarked = bookmarks.some((item) => item.talkId === talk.talkId);
      updateBookmark();
    })
    .catch(() => undefined);
  toolbar.append(
    analysisButton,
    highlightHelp,
    noteHelp,
    bookmarkTalk,
    shareTalk,
  );

  mainColumn.append(layout, sequence, liveStatus, toolbar);
  workspace.append(mainColumn, pane.element);
  page.append(workspace);
  const sharePath = options.targetParagraphId
    ? `/read/${talk.talkId}/${options.targetParagraphId}`
    : `/read/${talk.talkId}`;
  setShareMetadata({
    title: `${talk.title} by ${talk.speaker.name}`,
    description: options.targetParagraphId
      ? `Read the shared paragraph from ${talk.title}.`
      : `Read ${talk.title}, a ${index.title} message by ${talk.speaker.name}.`,
    pathname: sharePath,
  });

  const readingParagraphs = [
    ...article.querySelectorAll<HTMLElement>('.reader-paragraph'),
  ];
  let progressTimer = 0;
  let disposed = false;
  const updateProgress = (): void => {
    if (disposed) return;
    const headerOffset = 150;
    let currentIndex = 0;
    for (const [indexPosition, paragraph] of readingParagraphs.entries()) {
      if (paragraph.getBoundingClientRect().top <= headerOffset) {
        currentIndex = indexPosition;
      } else {
        break;
      }
    }
    const current = talk.paragraphs[currentIndex];
    const percent = ((currentIndex + 1) / readingParagraphs.length) * 100;
    talkHeader.updateProgress(percent);
    if (!studyAvailable) return;
    window.clearTimeout(progressTimer);
    progressTimer = window.setTimeout(() => {
      void studyStore
        .saveProgress({
          talkId: talk.talkId,
          paragraphId: current.id,
          percent,
          completed: percent >= 99.5,
        })
        .catch(() => {
          liveStatus.textContent = 'Reading progress could not be saved.';
        });
    }, 500);
  };
  window.addEventListener('scroll', updateProgress, { passive: true });
  updateProgress();

  const cleanupObserver = new MutationObserver(() => {
    if (page.isConnected || disposed) return;
    disposed = true;
    window.clearTimeout(progressTimer);
    window.removeEventListener('scroll', updateProgress);
    pane.destroy();
    cleanupObserver.disconnect();
  });
  cleanupObserver.observe(document.body, { childList: true, subtree: true });

  const restoreParagraph = (paragraphId: string): void => {
    const target = document.getElementById(paragraphId);
    if (!target) return;
    const alignTarget = (): void => {
      if (disposed || !target.isConnected) return;
      target.scrollIntoView({ block: 'start' });
      target.focus({ preventScroll: true });
    };
    window.requestAnimationFrame(alignTarget);
    void document.fonts.ready.then(() => {
      window.requestAnimationFrame(alignTarget);
    });
  };

  void studyStore
    .loadTalk(talk.talkId)
    .then((loadedState) => {
      if (disposed) return;
      studyAvailable = true;
      studyState = loadedState;
      for (const paragraph of talk.paragraphs) refreshParagraph(paragraph.id);
      talkHeader.updateProgress(loadedState.progress?.percent ?? 0);
      const storedParagraphId = loadedState.progress?.paragraphId;
      const restoreId = options.targetParagraphId ?? storedParagraphId ?? undefined;
      if (restoreId) {
        if (!options.targetParagraphId && storedParagraphId) {
          window.history.replaceState(
            {},
            '',
            `/read/${talk.talkId}/${storedParagraphId}${threadSuffix}`,
          );
        }
        restoreParagraph(restoreId);
      }
    })
    .catch((error: unknown) => {
      if (disposed) return;
      studyAvailable = false;
      if (options.targetParagraphId) restoreParagraph(options.targetParagraphId);
      liveStatus.textContent =
        error instanceof StudyStoreError && error.status === 401
          ? 'Sign in to save your study activity.'
          : 'Study storage is currently unavailable.';
    });

  return page;
}
